/**
 * @file useCanvasConsent.ts
 * @description Баннер согласия на cookie на канве конструктора (phase19.16,
 * партия Р3г, v0.19.62; SB134 «на канве баннер виден, пока открыта панель
 * «Счётчики и cookie»»; протокол — lib/site-view/canvas-consent-messages.ts).
 *
 * Пока раздел открыт (`active`), после каждой загрузки окна канвы и после
 * каждой правки (несохранённой тоже — значение из состояния части) кабинет
 * шлёт `commora:canvas-consent` с блоком согласия по черновику — по текстам
 * редакции черновика (вид баннера канва берёт из живой темы). Раздел
 * закрыли — одно сообщение «убрать» (`show: null`). Ответа канвы не ждём:
 * баннер — показ, а не запись. После загрузки окна команда повторяется через
 * `CANVAS_CONSENT_RESEND_MS`: окно могло загрузиться раньше, чем витрина
 * повесила слушатель (как у корзины, `useCanvasCart`).
 */

import { siteConsentBannerKind } from "@chaiberi/shared";
import { useCallback, useEffect, useMemo, useRef, type RefObject } from "react";

import type { SiteDraftParts } from "../site-view/useSiteDraftParts";
import { canvasConsentCloseMessage, canvasConsentMessage, type CanvasConsentMessage } from "../../lib/site-view/canvas-consent-messages";
import { postToCanvas } from "../../lib/site-view/canvas-messages";
import { bannerTextsFor, consentCanvasView, draftConsent } from "../../lib/site-view/consent-platform";

/** Повтор команды после загрузки окна канвы. */
export const CANVAS_CONSENT_RESEND_MS = 300;

interface Options {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  /** Открыт раздел «Счётчики и cookie», на канве свой черновик. */
  active: boolean;
  /** Части черновика (с несохранёнными правками); null — черновик не загружен. */
  parts: SiteDraftParts | null;
  /** Вкладка раздела из адреса: баннер или окно «Настроить». */
  sub: string | null;
  /** Ключ окна канвы: новый — окно пересоздано, ждём его загрузки. */
  loadKey: string;
}

export interface CanvasConsent {
  /** Окно канвы загрузилось (вызывать из `onLoad` iframe). */
  onLoad: () => void;
}

export function useCanvasConsent({ iframeRef, active, parts, sub, loadKey }: Options): CanvasConsent {
  const loaded = useRef(false);
  const resend = useRef<ReturnType<typeof setTimeout> | null>(null);
  const part = parts?.counters ?? null;
  const counters = part?.counters ?? null;
  const noticeAllowed = part?.noticeAllowed ?? true;
  const platform = part?.platform;
  const message = useMemo<CanvasConsentMessage | null>(() => {
    if (!active || !counters) return null;
    const view = consentCanvasView(sub, siteConsentBannerKind(counters, noticeAllowed));
    return canvasConsentMessage(view, draftConsent(counters, noticeAllowed, bannerTextsFor(counters, platform)));
  }, [active, counters, noticeAllowed, platform, sub]);
  const latest = useRef(message);
  latest.current = message;

  const stopResend = useCallback(() => {
    if (resend.current !== null) clearTimeout(resend.current);
    resend.current = null;
  }, []);

  // Новое окно канвы — ждём его загрузки, прежнему не шлём.
  useEffect(() => {
    loaded.current = false;
    stopResend();
  }, [loadKey, stopResend]);
  useEffect(() => stopResend, [stopResend]);

  // Правка или смена вида — сразу на канву; раздел закрыли — убрать баннер (один раз).
  const shown = useRef(false);
  useEffect(() => {
    if (!loaded.current) return;
    if (message) {
      postToCanvas(iframeRef.current, message);
      shown.current = true;
    } else if (shown.current) {
      stopResend();
      postToCanvas(iframeRef.current, canvasConsentCloseMessage());
      shown.current = false;
    }
  }, [iframeRef, message, stopResend]);

  const onLoad = useCallback(() => {
    loaded.current = true;
    // В новом окне баннера ещё нет: «убрать» не нужно.
    shown.current = latest.current !== null;
    if (!latest.current) return;
    postToCanvas(iframeRef.current, latest.current);
    stopResend();
    resend.current = setTimeout(() => {
      resend.current = null;
      if (loaded.current && latest.current) postToCanvas(iframeRef.current, latest.current);
    }, CANVAS_CONSENT_RESEND_MS);
  }, [iframeRef, stopResend]);

  return { onLoad };
}
